import React from 'react';
import { motion } from 'framer-motion';
import { 
  Heart, 
  MessageCircle, 
  Share, 
  Plus,
  Camera,
  MapPin,
  Clock
} from 'lucide-react';

export default function Social() {
  const posts = [
    {
      id: 1,
      user: {
        name: 'Sarah Chen',
        avatar: 'https://images.pexels.com/photos/774909/pexels-photo-774909.jpeg?auto=compress&cs=tinysrgb&w=100&h=100&fit=crop',
        level: 'Forest Guardian'
      },
      content: 'Just completed my daily bike commute! 🚴‍♀️ Feeling great about reducing my carbon footprint.',
      image: 'https://images.pexels.com/photos/100582/pexels-photo-100582.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop',
      mission: 'Car-Free Commute',
      points: 100,
      likes: 23,
      comments: 5,
      timeAgo: '2h ago',
      liked: false
    },
    {
      id: 2,
      user: {
        name: 'Marcus Rodriguez',
        avatar: 'https://images.pexels.com/photos/1043471/pexels-photo-1043471.jpeg?auto=compress&cs=tinysrgb&w=100&h=100&fit=crop',
        level: 'Eco Champion'
      },
      content: 'Made a delicious plant-based Buddha bowl today! 🥗 Who else is trying to eat more sustainably?',
      image: 'https://images.pexels.com/photos/1640777/pexels-photo-1640777.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop',
      mission: 'Plant-Based Meal',
      points: 75,
      likes: 31,
      comments: 8,
      timeAgo: '4h ago',
      liked: true
    },
    {
      id: 3,
      user: {
        name: 'Emma Thompson',
        avatar: 'https://images.pexels.com/photos/1040880/pexels-photo-1040880.jpeg?auto=compress&cs=tinysrgb&w=100&h=100&fit=crop',
        level: 'Tree'
      },
      content: 'Organized a beach cleanup with my local community group. Together we collected 50kg of plastic waste! 🌊',
      image: 'https://images.pexels.com/photos/2547565/pexels-photo-2547565.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop',
      mission: 'Community Clean-Up',
      points: 300,
      likes: 67,
      comments: 12,
      timeAgo: '1d ago',
      liked: false
    }
  ];

  return (
    <div className="h-full mobile-scroll custom-scrollbar">
      <div className="space-y-4 pb-6">
        {/* Create Post Button */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="p-4"
        >
          <button className="w-full bg-emerald-500 text-white p-4 rounded-2xl font-semibold flex items-center justify-center space-x-2 ripple card-shadow">
            <Plus className="h-5 w-5" />
            <span>Share Your Eco Action</span>
          </button>
        </motion.div>

        {/* Posts Feed */}
        <div className="space-y-4">
          {posts.map((post, index) => (
            <motion.div
              key={post.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
              className="bg-white card-shadow"
            >
              {/* Post Header */}
              <div className="p-4 pb-3">
                <div className="flex items-center space-x-3">
                  <img
                    src={post.user.avatar}
                    alt={post.user.name}
                    className="w-12 h-12 rounded-full"
                  />
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <h3 className="font-semibold text-gray-900">{post.user.name}</h3>
                      <span className="bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full text-xs font-medium">
                        {post.user.level}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-gray-500">
                      <Clock className="h-4 w-4" />
                      <span>{post.timeAgo}</span>
                    </div>
                  </div>
                </div>

                {/* Mission Badge */}
                <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-xl p-3">
                  <div className="flex items-center space-x-2">
                    <div className="bg-emerald-500 p-1 rounded">
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    </div>
                    <span className="text-sm font-medium text-emerald-700">
                      Completed: {post.mission}
                    </span>
                    <span className="bg-emerald-200 text-emerald-800 px-2 py-0.5 rounded-full text-xs font-medium">
                      +{post.points} pts
                    </span>
                  </div>
                </div>
              </div>

              {/* Post Content */}
              <div className="px-4 pb-3">
                <p className="text-gray-900 leading-relaxed">{post.content}</p>
              </div>

              {/* Post Image */}
              <div className="px-4 pb-3">
                <img
                  src={post.image}
                  alt="Post content"
                  className="w-full h-64 object-cover rounded-2xl"
                />
              </div>

              {/* Post Actions */}
              <div className="px-4 py-3 border-t border-gray-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-6">
                    <button className={`flex items-center space-x-2 ${post.liked ? 'text-red-500' : 'text-gray-600'}`}>
                      <Heart className={`h-5 w-5 ${post.liked ? 'fill-current' : ''}`} />
                      <span className="text-sm font-medium">{post.likes}</span>
                    </button>
                    
                    <button className="flex items-center space-x-2 text-gray-600">
                      <MessageCircle className="h-5 w-5" />
                      <span className="text-sm font-medium">{post.comments}</span>
                    </button>
                    
                    <button className="flex items-center space-x-2 text-gray-600">
                      <Share className="h-5 w-5" />
                    </button>
                  </div>

                  <span className="text-xs text-gray-500">
                    {post.likes} likes • {post.comments} comments
                  </span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Load More */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3, delay: 0.5 }}
          className="p-4"
        >
          <button className="w-full bg-gray-100 text-gray-700 p-3 rounded-2xl font-medium">
            Load More Posts
          </button>
        </motion.div>
      </div>
    </div>
  );
}